import { useQueryClient } from '@tanstack/react-query';

import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useRef, useState } from 'react';

import { ActionItemStatus } from '@ekhodealer/ekho-common';
import {
	Button,
	Callout,
	Divider,
	Group,
	InfoCircleFilled14,
	Stack,
	SuccessGraphic,
	Text,
} from '@ekhodealer/ekho-common/rev';

import type { ActionItem, GetProgressResponse } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { queries } from '@config/server/queryKeys';

import { useGetSellerRonSession } from '../../../api/progress/useGetSellerRonSession';
import { useStartSellerRonSession } from '../../../api/progress/useStartSellerRonSession';
import { navigateBackAtom, orderDetailsDrawerAtom } from '../../../atoms/orderDetailsDrawerAtoms';

interface NotaryLiveWindow extends Window {
	makeIframe?: (config: {
		target_id: string;
		token: string;
		height: string;
		width: string;
	}) => void;
}

const NOTARY_LIVE_ORIGIN = 'https://notarylive.com';
const NOTARY_LIVE_SDK_URL = 'https://notarylive.com/iframe/sdk/v1.js';

/** Minimum width (px) accepted by the NotaryLive SDK. */
const MIN_IFRAME_WIDTH = 600;

type SessionState = 'idle' | 'loading' | 'active' | 'completed' | 'error';

interface SellerRonSessionDetailProps {
	actionItem: ActionItem;
}

/**
 * SellerRonSessionDetail component for the SELLER_RON_SESSION action item.
 *
 * On mount, fetches the current seller RON session status from the backend:
 * - If the session is already `in_progress` with a whitelabelToken, the NotaryLive
 *   iframe is resumed immediately (no intro page).
 * - If the session is `not_started`, the intro/prep page is shown first and
 *   the CTA button calls startSellerRonSession to create a new session.
 */
export const SellerRonSessionDetail = ({ actionItem }: SellerRonSessionDetailProps) => {
	const { selectedRow } = useAtomValue(orderDetailsDrawerAtom);
	const orderId = selectedRow?.purchasedVehicleId;
	const navigateBack = useSetAtom(navigateBackAtom);

	const servicesProviderId = useCurrentServicesProviderId();
	const queryClient = useQueryClient();
	const startSessionMutation = useStartSellerRonSession(orderId);

	// Fetch the current session status from the backend
	const { data: sessionData, isLoading: isLoadingSession } = useGetSellerRonSession(orderId);

	const [sessionState, setSessionState] = useState<SessionState>('idle');
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const iframeWrapperRef = useRef<HTMLDivElement>(null);
	const sdkLoadedRef = useRef(false);
	/** Guard: prevents the completion handler from firing more than once. */
	const sessionCompletedRef = useRef(false);
	/** Guard: prevents the auto-resume effect from firing more than once. */
	const autoResumedRef = useRef(false);

	const isComplete = actionItem.status === ActionItemStatus.COMPLETE;

	// Load the NotaryLive SDK script
	const loadSdk = useCallback((): Promise<void> => {
		return new Promise((resolve, reject) => {
			if (sdkLoadedRef.current || (window as unknown as NotaryLiveWindow).makeIframe) {
				sdkLoadedRef.current = true;
				resolve();
				return;
			}
			const script = document.createElement('script');
			script.src = NOTARY_LIVE_SDK_URL;
			script.async = true;
			script.onload = () => {
				sdkLoadedRef.current = true;
				resolve();
			};
			script.onerror = () => reject(new Error('Failed to load NotaryLive SDK'));
			document.head.appendChild(script);
		});
	}, []);

	/**
	 * Launch the NotaryLive iframe with the given whitelabel token.
	 * Shared by both the "start new session" and "resume existing session" flows.
	 */
	const launchIframe = useCallback(
		async (token: string) => {
			setSessionState('loading');
			setErrorMessage(null);
			sessionCompletedRef.current = false;

			try {
				await loadSdk();

				// Small delay to let React commit the wrapper div
				setTimeout(() => {
					const wrapper = iframeWrapperRef.current;
					if (!(window as unknown as NotaryLiveWindow).makeIframe || !wrapper) {
						setErrorMessage(
							'Failed to initialize the notarization session. Please refresh and try again.',
						);
						setSessionState('error');
						return;
					}

					// Create the SDK target div *imperatively* inside the wrapper.
					// makeIframe() replaces the target element with an <iframe>,
					// which would break React's DOM tree if React were tracking
					// that element. By creating the target outside of React's
					// virtual DOM, the replacement only affects the wrapper's
					// children — which React does not manage.
					wrapper.innerHTML = '';
					const targetDiv = document.createElement('div');
					targetDiv.id = 'seller-notarylive-iframe-target';
					wrapper.appendChild(targetDiv);

					// NotaryLive SDK rejects percentage-based sizes and values < 600px.
					const rawWidth =
						wrapper.offsetWidth ||
						wrapper.parentElement?.offsetWidth ||
						window.innerWidth * 0.5;
					const containerWidth = Math.max(Math.floor(rawWidth), MIN_IFRAME_WIDTH);

					// The SDK's makeIframe() replaces the target div with an <iframe>.
					// It does NOT set the `allow` attribute, which is required for the
					// browser to grant camera/microphone access inside the cross-origin
					// frame. We temporarily patch document.createElement so the iframe
					// receives `allow` at birth.
					const origCreateElement = document.createElement.bind(document);
					document.createElement = function patchedCreate(
						tagName: string,
						options?: ElementCreationOptions,
					) {
						const el = origCreateElement(tagName, options);
						if (typeof tagName === 'string' && tagName.toLowerCase() === 'iframe') {
							el.setAttribute(
								'allow',
								'camera; microphone; display-capture; autoplay',
							);
						}
						return el;
					} as typeof document.createElement;

					try {
						(window as unknown as NotaryLiveWindow).makeIframe?.({
							target_id: 'seller-notarylive-iframe-target',
							token,
							height: '700px',
							width: `${containerWidth}px`,
						});
					} finally {
						document.createElement = origCreateElement;
					}

					// The SDK only accepts fixed pixel values, but we want the
					// iframe to fill the flex container. Override after creation.
					const iframe = wrapper.querySelector('iframe');
					if (iframe) {
						iframe.style.height = '100%';
						iframe.style.width = '100%';
						iframe.style.border = 'none';
					}
					wrapper.style.minHeight = '0';

					setSessionState('active');
				}, 100);
		} catch (err: unknown) {
			console.error('Error launching NotaryLive iframe:', err);
			setErrorMessage(
				err instanceof Error
					? err.message
					: 'Failed to initialize the notarization session. Please try again.',
			);
				setSessionState('error');
			}
		},
		[loadSdk],
	);

	/**
	 * Handle session completion: optimistically update cache.
	 * The user navigates back manually via the "Done" button.
	 */
	const handleSessionCompleted = useCallback(() => {
		if (sessionCompletedRef.current) return;
		sessionCompletedRef.current = true;

		setSessionState('completed');

		// Optimistically mark this action item as complete in the cached progress data
		// so the list shows the updated status immediately (the backend webhook from
		// NotaryLive may not have been processed yet).
		if (orderId && servicesProviderId) {
			const progressQueryKey = queries.orders.detail(servicesProviderId, orderId)._ctx
				.progress.queryKey;

			queryClient.setQueryData<GetProgressResponse>(progressQueryKey, (prev) => {
				if (!prev) return prev;
				const updatedProgress = { ...prev.progress };
				for (const operation of Object.keys(updatedProgress) as Array<
					keyof typeof updatedProgress
				>) {
					updatedProgress[operation] = updatedProgress[operation].map((item) => {
						if (item.actionCode !== actionItem.actionCode) return item;
						return Object.assign(Object.create(Object.getPrototypeOf(item)), item, {
							status: ActionItemStatus.COMPLETE,
							completedAt: Date.now(),
						}) as ActionItem;
					});
				}
				return { ...prev, progress: updatedProgress };
			});

			// Commenting this out for now to avoid race condition where re-fetch completes before action item states is recalculated.
			// queryClient.invalidateQueries({ queryKey: progressQueryKey });
		}
	}, [actionItem.actionCode, orderId, servicesProviderId, queryClient]);

	// Start a brand-new RON session (called from the CTA button)
	const handleStartSession = useCallback(async () => {
		try {
			const result = await startSessionMutation.mutateAsync();

			// Invalidate the GET session query so a future mount (or retry)
			// will see the now-in-progress session and auto-resume.
			if (orderId && servicesProviderId) {
				queryClient.invalidateQueries({
					queryKey: queries.orders.detail(servicesProviderId, orderId)._ctx.progress._ctx
						.sellerRonSession.queryKey,
				});
			}

			await launchIframe(result.whitelabelToken);
		} catch (err: unknown) {
			console.error('Error starting seller RON session:', err);
			setErrorMessage(
				err instanceof Error
					? err.message
					: 'Failed to start counter-signing session. Please try again.',
			);
			setSessionState('error');
		}
	}, [startSessionMutation, launchIframe, orderId, servicesProviderId, queryClient]);

	// Listen for postMessage events from the NotaryLive iframe
	useEffect(() => {
		const handleMessage = (event: MessageEvent) => {
			if (event.origin !== NOTARY_LIVE_ORIGIN) return;

			const eventType = event.data?.type || event.data?.event;
			if (!eventType) return;

			switch (eventType) {
				case 'NL-session-complete':
				case 'documents_notarized':
					handleSessionCompleted();
					break;
				case 'NL-order-expired':
					handleStartSession();
					break;
				case 'order_canceled':
					setSessionState('error');
					setErrorMessage('The notarization session was canceled. Please try again.');
					break;
				default:
					break;
			}
		};

		window.addEventListener('message', handleMessage);
		return () => window.removeEventListener('message', handleMessage);
	}, [handleSessionCompleted, handleStartSession]);

	// Auto-resume: if the backend reports an in_progress session with a token,
	// skip the intro and launch the iframe immediately.
	useEffect(() => {
		if (autoResumedRef.current || isLoadingSession || !sessionData || sessionState !== 'idle') {
			return;
		}

		if (sessionData.sessionStatus === 'in_progress' && sessionData.whitelabelToken) {
			autoResumedRef.current = true;
			launchIframe(sessionData.whitelabelToken);
		}
	}, [sessionData, isLoadingSession, sessionState, launchIframe]);

	if (isComplete) {
		return (
			<Stack itemsSpacing='16'>
				<Text size='body' weight='semibold'>
					{actionItem.config.title}
				</Text>
				<Text size='bodySmall' color='secondary'>
					The counter-signing RON session has been completed successfully.
				</Text>
			</Stack>
		);
	}

	// While the GET request is in flight, show a brief loading state
	if (isLoadingSession) {
		return (
			<Stack itemsSpacing='8'>
				<Text size='bodySmall' color='secondary'>
					Checking session status…
				</Text>
			</Stack>
		);
	}

	return (
		<div
			style={{
				display: 'flex',
				flexDirection: 'column',
				height: 'calc(100vh - 180px)',
				gap: '16px',
			}}>
			{/* ── Pre-session info (visible before the session starts) ── */}
			{sessionState === 'idle' && (
				<Stack itemsSpacing='32'>
					<Text size='body' color='secondary'>
						You'll need to complete a live video notarization session.
					</Text>

					{/* What to prepare */}
					<Stack itemsSpacing='12'>
						<Text size='body' weight='semibold'>
							What to prepare
						</Text>
						<Text size='bodySmall' color='secondary'>
							Government-issued photo ID (Passport or driver's license)
						</Text>
						<Callout
							variant='neutral'
							appearance='outline'
							title='Before you start'
							message='Make sure your camera, microphone, and internet connection are working.'
							customIcon={
								<span style={{ color: 'var(--rev-color-infoBase, #60a5fa)' }}>
									<InfoCircleFilled14 />
								</span>
							}
						/>
					</Stack>

					<Divider />

					{/* What to expect */}
					<Stack itemsSpacing='12'>
						<Text size='body' weight='semibold'>
							What to expect
						</Text>
						<ol
							style={{
								color: 'var(--rev-color-textSecondary)',
								margin: 0,
								paddingLeft: '20px',
								fontSize: 'var(--rev-fontSize-tempBody, 14px)',
								lineHeight: 1.5,
							}}>
							<li style={{ marginBottom: '4px' }}>
								Review your documents and confirm that all information is accurate.
							</li>
							<li style={{ marginBottom: '4px' }}>
								Take a selfie or answer a few personal questions to confirm your
								identity.
							</li>
							<li style={{ marginBottom: '4px' }}>
								Join a short, secure video call with a licensed notary.
							</li>
							<li>Present your ID and sign documents.</li>
						</ol>
						<Text size='bodySmall' color='secondary'>
							Most sessions take <span style={{ fontWeight: 600 }}>5–10 minutes</span>
							.
						</Text>
					</Stack>

					{/* Action Buttons */}
					<Group itemsSpacing='8' noWrap>
						<Group.Item grow={1}>
							<Button
								variant='secondary'
								size='large'
								isFullWidth
								onPress={navigateBack}>
								Cancel
							</Button>
						</Group.Item>
						<Group.Item grow={1}>
							<Button
								size='large'
								isFullWidth
								onPress={handleStartSession}
								isLoading={startSessionMutation.isPending}
								isDisabled={startSessionMutation.isPending}>
								Begin notarization
							</Button>
						</Group.Item>
					</Group>
				</Stack>
			)}

			{/* ── Error ── */}
			{errorMessage && (
				<Text size='bodySmall' color='error'>
					{errorMessage}
				</Text>
			)}

			{/* ── Loading copy (no spinner — loading indicator is on the button) ── */}
			{sessionState === 'loading' && (
				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						padding: '48px 0',
						gap: '8px',
					}}>
					<Text size='bodySmall' color='secondary'>
						Setting up the notarization session...
					</Text>
					<Text size='bodySmall' color='secondary'>
						This may take a few moments. Please do not close this page.
					</Text>
				</div>
			)}

			{/* ── Completed ── */}
			{sessionState === 'completed' && (
				<Stack itemsSpacing='24'>
					<div
						style={{
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							padding: '48px 0',
							gap: '16px',
						}}>
						<SuccessGraphic />
						<Text size='body' weight='semibold' color='success'>
							Counter-signing session completed!
						</Text>
						<Text size='bodySmall' color='secondary'>
							The notarized documents have been submitted.
						</Text>
					</div>
					<Button size='large' isFullWidth onPress={navigateBack}>
						Done
					</Button>
				</Stack>
			)}

			{/* ── Error retry ── */}
			{sessionState === 'error' && (
				<Button variant='filled' onPress={handleStartSession}>
					Try Again
				</Button>
			)}

			{/* Wrapper div is always in the DOM and managed by React.
			   The SDK target div is created *imperatively* inside it (not via JSX),
			   so makeIframe() can safely replace that inner div with an <iframe>
			   without breaking React's DOM tree.
			   Visible only when active; off-screen during loading so makeIframe()
			   has a real element to target; hidden otherwise. */}
			<div
				ref={iframeWrapperRef}
				style={{
					border: '1px solid #ccc',
					borderRadius: '4px',
					overflow: 'hidden',
					boxShadow: 'none',
					...(sessionState === 'active'
						? {
								width: '100%',
								flex: 1,
								minHeight: 0,
								display: 'block',
						  }
						: sessionState === 'loading'
						? {
								position: 'fixed' as const,
								left: '-9999px',
								width: '800px',
								height: '700px',
						  }
						: { display: 'none' }),
				}}
			/>
		</div>
	);
};
